<template>
    <div>
        <el-form ref="form" :model="form" v-loading="isBusy" :rules="rules">
            <el-form-item label="Send List" prop="send_list_id">
                <el-select v-model="form.send_list_id"
                           class="w-100"
                           filterable
                           clearable
                           remote
                           placeholder="Search"
                           :remote-method="searchSendList"
                           :loading="sendListLoading"
                           :disabled="!canChangeSendList"
                >
                    <el-option
                            v-for="item in sendLists"
                            :key="item.id"
                            :label="item.name"
                            :value="item.id"
                    >
                    </el-option>
                </el-select>
            </el-form-item>

            <el-form-item label="Status" prop="status">
                <el-select v-model="form.status" class="w-100" :disabled="!canChangeStatus">
                    <el-option
                            v-for="item in statuses"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                    >
                    </el-option>
                </el-select>
            </el-form-item>

            <el-form-item label="Start At" prop="start_at">
                <el-date-picker
                        v-model="form.start_at"
                        type="datetime"
                        placeholder="Start"
                        :disabled="!canChangeStartAt"
                        :format="dateFormat"
                        :value-format="formDateFormat"
                ></el-date-picker>
            </el-form-item>

            <el-form-item label="Stop At" prop="stop_at">
                <el-date-picker
                        v-model="form.stop_at"
                        type="datetime"
                        placeholder="Stop"
                        :disabled="!canChangeStopAt"
                        :format="dateFormat"
                        :value-format="formDateFormat"
                ></el-date-picker>
            </el-form-item>

            <el-form-item label="Message" prop="body">
                <el-input
                        ref="body"
                        v-model="form.body"
                        type="textarea"
                        :autosize="{minRows: 2, maxRows: 6}"
                        :disabled="!canChangeBody"
                ></el-input>
                <message-tools @insert-value="insertValueIntoMessage"></message-tools>
            </el-form-item>

            <el-form-item>
                <el-button type="primary" @click="save">Save</el-button>
            </el-form-item>
        </el-form>
    </div>
</template>

<script>
  import ValidatorMixin from "../../mixins/validator";
  import MessageTools from "./../shared/message-tools";
  import Inputs from "../../mixins/inputs";

  export default {
    components: {
      MessageTools,
    },

    mixins: [
      Inputs,
      ValidatorMixin,
    ],

    props: {
      initialModel: {
        type: Object,
        default: function () {
          return {};
        },
      },
      initialSendLists: {
        type: Array,
        default: function () {
          return [];
        },
      },
      sendListsSearchUrl: String,
      initialStatuses: {
        type: Array,
        default: function () {
          return [];
        },
      },
      storeUrl: {
        type: String,
      },
      introMessage: {
        type: null,
        default: null,
      },
      constants: Object,
    },

    data() {
      const form = {
        send_list_id: null,
        status: null,
        start_at: null,
        stop_at: null,
        body: null,
      };

      return {
        form,
        model: {},
        sendLists: [],
        sendListLoading: false,
        statuses: [],
        rules: _.mapValues(form, () => [{validator: this.fieldExternalValidator}]),
        errors: {},
        isBusy: false,
      };
    },

    computed: {
      /**
       * @return {String}
       */
      dateFormat() {
        return config('format.datetime');
      },
      /**
       * @return {String}
       */
      formDateFormat() {
        return config('format.isoLocal');
      },

      /**
       * @return {Boolean}
       */
      isEditMode() {
        return !isNaN(_.get(this.model, 'id'));
      },

      /**
       * @return {String}
       */
      method() {
        return this.isEditMode ? 'PATCH' : 'POST';
      },

      /**
       * @return {Boolean}
       */
      canChangeSendList() {
        return _.includes([this.constants.status.DRAFT], this.model.status);
      },

      /**
       * @return {Boolean}
       */
      canChangeStatus() {
        return !_.includes([this.constants.status.CANCELED], this.model.status);
      },

      /**
       * @return {Boolean}
       */
      canChangeStartAt() {
        return _.includes([this.constants.status.DRAFT], this.model.status);
      },

      /**
       * @return {Boolean}
       */
      canChangeStopAt() {
        return _.includes([this.constants.status.DRAFT, this.constants.status.PUBLISHED], this.model.status);
      },

      /**
       * @return {Boolean}
       */
      canChangeBody() {
        return _.includes([this.constants.status.DRAFT], this.model.status);
      },

      /**
       * @return Boolean
       */
      isStatusDirty() {
        return this.model.status !== this.form.status;
      },
    },

    watch: {
      model(value) {
        this.initForm(value);
      },
    },

    methods: {
      /**
       * @param {String} text
       */
      searchSendList(text) {
        this.sendListLoading = true;
        axios.get(this.sendListsSearchUrl, {params: {search: text}})
          .then(({data: {data}}) => this.sendLists = data)
          .catch(() => this.sendLists = [])
          .finally(() => this.sendListLoading = false);
      },

      /**
       * @return {Object}
       */
      getForm() {
        return _.assign({}, this.form, {_method: this.method});
      },

      /**
       * @return {undefined}
       */
      save() {
        if (this.isStatusDirty && this.form.status === this.constants.status.PUBLISHED) {
          this.confirmSavingPublished();
        } else {
          this.store(false);
        }
      },

      /**
       * @return {undefined}
       */
      confirmSavingPublished() {
        this.$confirm('Do you confirm saving the bulk message in the status published?')
          .then(() => this.store(false))
          .catch(() => {
          });
      },

      /**
       * @return {undefined}
       */
      store(skipTimeWindowCheck = false) {
        this.isBusy = true;
        const params = skipTimeWindowCheck ? {skip_time_window_check: 1} : {};
        const form = _.assign({}, this.getForm(), params);

        axios.post(this.storeUrl, form)
          .then(this.storeSuccess)
          .catch(this.handleErrorResponse)
          .finally(() => this.isBusy = false);
      },

      /**
       * @return {undefined}
       */
      storeSuccess({data: {data, redirect, message, statuses}}) {
        if (redirect) {
          window.location.replace(redirect);

          return;
        }

        this.model = data;
        if (statuses) {
          this.statuses = statuses
        }
        message && this.$message(message);
      },

      /**
       * @param {Object} response
       */
      handleErrorResponse({response: {data, status}}) {
        const {errors, message} = data || {};
        if (status === 422 && errors) {
          this.errors = errors;
          this.$refs.form.validate(() => null);
          this.$message.error(message);
        } else if (status === 428) {
          this.handleTimeWindowError(data);
        } else if (message && _.isString(message)) {
          this.$message.error(message);
        }
      },

      /**
       * @param {Object} model
       */
      initForm(model) {
        const keys = ['send_list_id', 'status', 'body'];
        const blank = _.mapValues(this.form, () => null);
        this.form = _.assign(blank, _.pick(this.model, keys));
        this.form.start_at = _.get(this.model, 'start_at.datetime');
        this.form.stop_at = _.get(this.model, 'stop_at.datetime');
      },

      /**
       * @param {Object} value
       */
      handleTimeWindowError({message}) {
        this.$confirm(message, 'Warning', {
          confirmButtonText: 'Ignore',
          cancelButtonText: 'Back',
          type: 'warning'
        })
          .then(() => {
            this.store(true);
          })
          .catch(() => {
          });
      },

      /**
       * @param {string} value
       */
      insertValueIntoMessage(value) {
        if (!this.canChangeBody) {
          return;
        }

        this.addTextInCursorPosition(
          _.get(this.$refs, 'body.$refs.textarea'),
          _.toString(this.form.body),
          value,
          this.messageSetter
        );
      },

      /**
       * @param {string} value
       */
      messageSetter(value) {
        this.form.body = value;
      },
    },

    mounted() {
      this.model = _.assign({}, this.initialModel);
      this.sendLists = this.initialSendLists;
      this.statuses = this.initialStatuses;
      this.introMessage && this.$message(this.introMessage);
    },
  }
</script>

